import { ChevronDown, ChevronUp, Clock } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { cx } from "@/utils/cx";

type OdometerTimePickerProps = {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	className?: string;
	isInvalid?: boolean;
};

const TIME_MENU_HEIGHT = 240;

function normalizeTimeInput(rawValue: string): string {
	const digits = rawValue.replace(/\D/g, "").slice(0, 4);
	if (digits.length === 0) {
		return "";
	}

	if (digits.length <= 2) {
		const hour = Number(digits);
		if (Number.isNaN(hour)) {
			return "";
		}
		return String(Math.min(hour, 23)).padStart(digits.length, "0");
	}

	const hourPart = String(Math.min(Number(digits.slice(0, 2)), 23)).padStart(
		2,
		"0"
	);
	const minuteDigits = digits.slice(2, 4);
	if (minuteDigits.length === 0) {
		return hourPart;
	}
	const minutePart = String(Math.min(Number(minuteDigits), 59)).padStart(
		minuteDigits.length,
		"0"
	);

	return `${hourPart}:${minutePart}`;
}

function getHourAndMinute(value: string): { hour: number; minute: number } {
	const [rawHour, rawMinute] = value.split(":");
	const hour = Number(rawHour);
	const minute = Number(rawMinute);
	if (
		Number.isNaN(hour) ||
		Number.isNaN(minute) ||
		hour < 0 ||
		hour > 23 ||
		minute < 0 ||
		minute > 59
	) {
		return { hour: 0, minute: 0 };
	}
	return { hour, minute };
}

function formatTime(hour: number, minute: number): string {
	return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function OdometerTimePicker({
	value,
	onChange,
	placeholder = "Escolha a hora",
	className,
	isInvalid = false,
}: OdometerTimePickerProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [openUpward, setOpenUpward] = useState(false);
	const rootRef = useRef<HTMLDivElement | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const { hour, minute } = getHourAndMinute(value);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const handleClickOutside = (event: MouseEvent) => {
			if (!rootRef.current) {
				return;
			}
			if (!rootRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen]);

	const handleOpen = () => {
		if (!rootRef.current) {
			setIsOpen(true);
			return;
		}
		const rect = rootRef.current.getBoundingClientRect();
		const availableBottom = window.innerHeight - rect.bottom;
		setOpenUpward(availableBottom < TIME_MENU_HEIGHT);
		setIsOpen(true);
	};

	const updateTime = (nextHour: number, nextMinute: number) => {
		const safeHour = Math.max(0, Math.min(nextHour, 23));
		const safeMinute = Math.max(0, Math.min(nextMinute, 59));
		onChange(formatTime(safeHour, safeMinute));
	};

	return (
		<div className="relative w-full" ref={rootRef}>
			<div
				className={cx(
					"flex h-10 w-full items-center gap-2 rounded-md border border-border-primary bg-primary px-3.5 py-1 shadow-xs",
					!!isInvalid &&
						"border-error_subtle ring-error_subtle dark:ring-error_subtle",
					className
				)}
			>
				<Clock className="size-5 text-fg-quaternary" />
				<input
					className="w-full bg-transparent font-medium text-fg-primary text-md outline-none placeholder:text-placeholder"
					onChange={(event) => onChange(normalizeTimeInput(event.target.value))}
					onFocus={handleOpen}
					placeholder={placeholder}
					ref={inputRef}
					value={value}
				/>
			</div>

			{isOpen ? (
				<div
					className={cx(
						"absolute z-30 w-[200px] rounded-md border border-border-secondary_alt bg-primary px-4 py-2 shadow-xl",
						openUpward ? "bottom-[calc(100%+4px)]" : "top-[calc(100%+4px)]"
					)}
				>
					<div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
						<div className="flex flex-col items-center">
							<button
								aria-label="Aumentar hora"
								className="rounded p-1 hover:bg-active"
								onClick={() => {
									updateTime((hour + 1) % 24, minute);
								}}
								type="button"
							>
								<ChevronUp className="size-5" />
							</button>
							<span className="font-semibold text-xl">
								{String(hour).padStart(2, "0")}
							</span>
							<button
								aria-label="Diminuir hora"
								className="rounded p-1 hover:bg-active"
								onClick={() => {
									updateTime((hour + 23) % 24, minute);
								}}
								type="button"
							>
								<ChevronDown className="size-5" />
							</button>
						</div>
						<span className="pt-1 font-semibold text-xl">:</span>
						<div className="flex flex-col items-center">
							<button
								aria-label="Aumentar minuto"
								className="rounded p-1 hover:bg-active"
								onClick={() => {
									updateTime(hour, (minute + 1) % 60);
								}}
								type="button"
							>
								<ChevronUp className="size-5" />
							</button>
							<span className="font-semibold text-xl">
								{String(minute).padStart(2, "0")}
							</span>
							<button
								aria-label="Diminuir minuto"
								className="rounded p-1 hover:bg-active"
								onClick={() => {
									updateTime(hour, (minute + 59) % 60);
								}}
								type="button"
							>
								<ChevronDown className="size-5" />
							</button>
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
}
